import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

export default function GoogleSignInButton({ onSuccess, text = 'continue_with', testId = 'google-signin-btn' }) {
  const { loginWithGoogleCredential } = useAuth();
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !containerRef.current || !window.google?.accounts?.id) {
      return;
    }

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: async ({ credential }) => {
        if (!credential) {
          toast.error('Google sign-in did not return a credential.');
          return;
        }
        setLoading(true);
        try {
          const user = await loginWithGoogleCredential(credential);
          if (onSuccess) {
            await onSuccess(user);
          }
        } catch (error) {
          toast.error(error.response?.data?.detail || 'Google sign-in failed.');
        } finally {
          setLoading(false);
        }
      },
      cancel_on_tap_outside: true,
      auto_select: false,
    });

    containerRef.current.innerHTML = '';
    window.google.accounts.id.renderButton(containerRef.current, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text,
      shape: 'rectangular',
      width: 320,
      logo_alignment: 'left',
    });
  }, [loginWithGoogleCredential, onSuccess, text]);

  if (!GOOGLE_CLIENT_ID) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div ref={containerRef} data-testid={testId} className="flex justify-center" />
      {loading && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span>Signing you in...</span>
        </div>
      )}
    </div>
  );
}
